import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { swatchHex, same, colourIndex, numbersIn, variantImageFor } from '@theme/lc-watering-logic';

/**
 * <lc-pdp-options>
 *
 * Two-step buy box for products sold by colour and one further option (the design's Nesting
 * Pads and Heated Bases PDPs): colour → pack size, or size → colour. Colours are theme-editor
 * blocks; each may carry its own Shopify product (the store sells nesting pads as one product
 * per colour) or the page's product supplies the colours through its own Colour option. The
 * second step's values, prices and images always come from the resolved product's variants.
 *
 * Preview mode: while no product is connected (heated bases are not in the catalogue yet) the
 * option values come from Option blocks, the price reads "Price coming soon" and the CTA is a
 * Notify-me link — nothing is invented.
 *
 * The selected colour is published on <html data-lc-colour> and as an `lc:colour` event so the
 * page's imagery (<lc-colour-swap>) can follow it.
 */

/** @param {unknown} value */
const esc = (value) =>
  String(value ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c
  );

const CHECK = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#058346" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';
const MINUS = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/></svg>';
const PLUS = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/><path d="M12 5v14"/></svg>';

/**
 * @typedef {import('@theme/lc-watering-logic').Product} Product
 * @typedef {import('@theme/lc-watering-logic').Variant} Variant
 * @typedef {{ name: string, hex: string, image: string, product: Product | null }} ColourEntry
 * @typedef {{ value: string, note: string }} OptionNote
 */

class LcPdpOptions extends HTMLElement {
  /** @type {{ colours: ColourEntry[], product: Product | null, options: OptionNote[], currentColour: string, colourFirst: boolean, labelFormat: string, unitLabel: string, bestValue: string, notifyUrl: string, fallbackImage: string, moneyFormat: string, currency: string, text: Record<string, string> }} */
  config = {
    colours: [],
    product: null,
    options: [],
    currentColour: '',
    colourFirst: true,
    labelFormat: '[value]',
    unitLabel: '',
    bestValue: '',
    notifyUrl: '',
    fallbackImage: '',
    moneyFormat: '${{amount}}',
    currency: 'USD',
    text: {},
  };

  state = {
    colour: /** @type {string | null} */ (null),
    value: /** @type {string | null} */ (null),
    qty: 1,
    busy: false,
    added: false,
    error: '',
  };

  #media = window.matchMedia('(max-width: 989px)');
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #panelVisible = true;

  connectedCallback() {
    const script = this.querySelector('script[data-lc-config]');
    if (script?.textContent) {
      try {
        this.config = { ...this.config, ...JSON.parse(script.textContent) };
      } catch (error) {
        console.warn('[lc-pdp-options] invalid config', error);
      }
    }
    const entries = this.entries();
    const current = entries.find((e) => same(e.name, this.config.currentColour)) ?? entries[0] ?? null;
    this.state.colour = current?.name ?? null;
    const values = this.values();
    this.state.value = values.find((v) => this.variantFor(this.state.colour, v)?.available) ?? values[0] ?? null;
    // A Shopify ?variant= URL (Shopping / ad landing pages) opens on that exact variant, even
    // when it is sold out, so the page agrees with the feed that linked to it.
    const requested = Number(this.config.selectedVariantId) || 0;
    const product = this.productFor(this.state.colour);
    const hit = requested && product ? product.variants.find((v) => v.id === requested) : null;
    if (hit) {
      const ci = colourIndex(product);
      const oi = this.optionIndex(product);
      if (ci >= 0 && this.entryFor(hit.options[ci])) this.state.colour = hit.options[ci];
      if (oi >= 0) this.state.value = hit.options[oi];
    }

    this.addEventListener('click', this.#onClick);
    this.#media.addEventListener('change', () => this.render());
    this.setAttribute('data-ready', 'true');
    this.render();
    this.#watchBuyPanel();
    this.#watchCart();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  get isMobile() {
    return this.#media.matches;
  }

  /** @param {number} cents */
  money(cents) {
    return formatMoney(Math.round(cents), this.config.moneyFormat, this.config.currency);
  }

  /** @param {Partial<typeof this.state>} patch */
  setState(patch) {
    Object.assign(this.state, patch);
    // Once the cart is known, "added" always follows the exact variant now selected (colour, size
    // and quantity changes included); the add flow itself passes `busy` and is left alone.
    if (this.#cartSynced && !('busy' in patch)) this.state.added = this.inCart(this.setup().variantId);
    this.render();
  }

  // ------------------------------------------------------------- cart truth
  // The "✓ Added to cart" state is only ever shown for the exact variant the CTA would add, and
  // only while that variant is in the Shopify cart. Every cart event (drawer remove, quantity
  // change, another buy box or the recommendations adding) carries the cart's line items, so the
  // buy box reconciles from the event; bfcache restores and tab returns re-read /cart.js.

  /** Quantity in the cart per variant id, as last reported by a cart event or /cart.js. */
  #cartQty = new Map();
  #cartSynced = false;

  #watchCart() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    // Only read the cart on load when the header already reports items in it.
    let count = 0;
    try {
      count = Number(sessionStorage.getItem('cart-count')) || 0;
    } catch {}
    if (count > 0 || document.querySelector('cart-icon [ref="cartBubbleCount"]:not(.hidden)')) this.#refreshCart();
  }

  /** @param {Event} event */
  #onCartLinesUpdate = (event) => {
    const promise = /** @type {{ promise?: Promise<any> }} */ (event).promise;
    const apply = (/** @type {any} */ result) => {
      const items = result?.detail?.items;
      if (Array.isArray(items)) this.#applyCart(items);
      else this.#refreshCart();
    };
    if (promise && typeof promise.then === 'function') promise.then(apply, () => this.#refreshCart());
    else this.#refreshCart();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#refreshCart();
  };

  #onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && (this.#cartSynced || this.state.added)) this.#refreshCart();
  };

  #refreshCart = async () => {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) return;
      const cart = await response.json();
      this.#applyCart(Array.isArray(cart.items) ? cart.items : []);
    } catch (error) {
      console.warn('[lc-pdp-options] cart re-sync failed', error);
    }
  };

  /** @param {Array<{ variant_id?: number, id?: number, quantity?: number }>} items */
  #applyCart(items) {
    const next = new Map();
    for (const item of items) {
      const id = Number(item.variant_id ?? item.id) || 0;
      if (id) next.set(id, (next.get(id) ?? 0) + (Number(item.quantity) || 0));
    }
    this.#cartQty = next;
    this.#cartSynced = true;
    this.#syncAdded();
  }

  /** True when the exact variant the CTA would add is in the cart right now. */
  inCart(variantId) {
    return Boolean(variantId) && (this.#cartQty.get(Number(variantId)) ?? 0) > 0;
  }

  #syncAdded() {
    if (this.state.busy) return;
    const added = this.inCart(this.setup().variantId);
    if (added !== this.state.added) this.setState({ added });
  }

  // ------------------------------------------------------------------ data

  /** Colourways: theme-editor blocks, else the page product's own Colour option. */
  entries() {
    if (this.config.colours.length) return this.config.colours;
    const product = this.config.product;
    const ci = colourIndex(product);
    if (!product || ci < 0) return [];
    return product.options[ci].values.map((name) => ({ name, hex: swatchHex(name), image: '', product }));
  }

  /** @param {string | null} colour */
  entryFor(colour) {
    if (!colour) return null;
    return this.entries().find((e) => same(e.name, colour)) ?? null;
  }

  /** The product the current colour resolves against (null in preview mode). */
  /** @param {string | null} [colour] */
  productFor(colour = this.state.colour) {
    if (this.config.colours.length) return this.entryFor(colour)?.product ?? null;
    return this.config.product;
  }

  /** True once at least one colourway has a live product (i.e. not preview mode). */
  get hasProducts() {
    return this.entries().some((e) => e.product);
  }

  /**
   * A colourway that cannot be bought right now: its product is not published to the
   * storefront yet, or every variant is sold out. Only meaningful once other colours are live.
   * @param {ColourEntry | null} entry
   */
  isSoldOut(entry) {
    if (!entry || !this.hasProducts) return false;
    if (!entry.product) return true;
    return !entry.product.variants.some((v) => v.available);
  }

  /** Index of the non-colour option (pack size, size…) on a product. */
  /** @param {Product | null} product */
  optionIndex(product) {
    if (!product || product.hasOnlyDefaultVariant) return -1;
    return product.options.findIndex((o) => !/colou?r/i.test(o.name));
  }

  /** Values for the second step: the product's option values, else the preview Option blocks. */
  /** @param {string | null} [colour] */
  values(colour = this.state.colour) {
    const product = this.productFor(colour);
    const oi = this.optionIndex(product);
    if (product && oi >= 0) return product.options[oi].values;
    if (product) return [];
    // A colour without a live product borrows its siblings' option values (shown as sold out).
    const sibling = this.entries().find((e) => e.product)?.product ?? null;
    const si = this.optionIndex(sibling);
    if (sibling && si >= 0) return sibling.options[si].values;
    return this.config.options.map((o) => o.value).filter(Boolean);
  }

  /** @param {string | null} colour @param {string | null} value */
  variantFor(colour, value) {
    const product = this.productFor(colour);
    if (!product) return null;
    const ci = colourIndex(product);
    const oi = this.optionIndex(product);
    return (
      product.variants.find(
        (v) => (ci < 0 || !colour || same(v.options[ci], colour)) && (oi < 0 || !value || same(v.options[oi], value))
      ) ?? null
    );
  }

  /** @param {string} value */
  labelFor(value) {
    const n = numbersIn(value)[0];
    return (this.config.labelFormat || '[value]').replace('[value]', value).replace('[n]', n === undefined ? '' : String(n));
  }

  /** @param {string} value */
  noteFor(value) {
    const n = numbersIn(value)[0];
    const hit = this.config.options.find((o) => same(o.value, value) || (n !== undefined && numbersIn(o.value)[0] === n));
    return hit?.note ?? '';
  }

  /** "$5.75 a pad" — only when a unit label is configured and the value carries a count. */
  /** @param {string} value @param {Variant | null} variant */
  unitFor(value, variant) {
    const n = numbersIn(value)[0];
    if (!this.config.unitLabel || !variant || !n) return '';
    return `${this.money(variant.price / n)} ${this.config.unitLabel}`;
  }

  /** @param {string} value */
  isBestValue(value) {
    const best = this.config.bestValue;
    if (!best) return false;
    const n = numbersIn(value)[0];
    return same(best, value) || (n !== undefined && numbersIn(best)[0] === n);
  }

  /** @param {string | null} colour */
  imageFor(colour) {
    const entry = this.entryFor(colour);
    const product = this.productFor(colour);
    return (product && colour ? variantImageFor(product, colour) : '') || product?.image || entry?.image || this.config.fallbackImage || '';
  }

  /** Current selection resolved. */
  setup() {
    const st = this.state;
    const product = this.productFor(st.colour);
    const values = this.values();
    const soldOutColour = this.isSoldOut(this.entryFor(st.colour));
    const preview = !product && !soldOutColour;
    const needsValue = values.length > 0;
    const valueOk = !needsValue || (st.value !== null && values.some((v) => same(v, st.value)));
    const variant = product ? this.variantFor(st.colour, valueOk ? st.value : null) : null;
    const entries = this.entries();
    const colourOk = entries.length === 0 || Boolean(this.entryFor(st.colour));
    const ready = colourOk && valueOk;
    return {
      product,
      preview,
      soldOutColour,
      values,
      variant,
      ready,
      colourOk,
      valueOk,
      price: variant?.price ?? 0,
      compare: variant && variant.compareAtPrice > variant.price ? variant.compareAtPrice : 0,
      available: variant ? variant.available : false,
      variantId: variant?.id ?? null,
      image: (variant?.image || this.imageFor(st.colour)) ?? '',
    };
  }

  // ------------------------------------------------------------------ render

  render() {
    const s = this.setup();
    const st = this.state;

    document.documentElement.dataset.lcColour = st.colour ?? '';
    document.dispatchEvent(new CustomEvent('lc:colour', { detail: { colour: st.colour ?? '' } }));

    const main = this.querySelector('.lc-obx__main');
    if (main instanceof HTMLImageElement && s.image && main.src !== s.image) {
      main.removeAttribute('srcset');
      main.src = s.image;
    }

    const thumbs = this.querySelector('[data-lc-thumbs]');
    if (thumbs) {
      const entries = this.entries();
      thumbs.hidden = entries.length < 2;
      thumbs.innerHTML = entries
        .map((e) => {
          const img = this.imageFor(e.name);
          return `<button type="button" class="lc-obx__thumb" data-action="colour" data-value="${esc(e.name)}" aria-pressed="${same(st.colour, e.name)}" aria-label="${esc(e.name)}">${img ? `<img src="${esc(img)}" alt="" width="220" height="220" loading="lazy">` : ''}</button>`;
        })
        .join('');
    }

    const steps = this.querySelector('[data-lc-steps]');
    if (steps) steps.innerHTML = this.renderSteps(s);

    const result = this.querySelector('[data-lc-result]');
    if (result) result.innerHTML = this.renderResult(s);

    this.renderSticky(s);

    // "In stock" badge follows the resolved variant's availability, not the product as a whole.
    // In preview mode (no product connected) it falls back to the page product's availability.
    const stockBadge = this.querySelector('[data-lc-stock-badge]');
    const inStock = !s.soldOutColour && (s.variantId ? !!s.available : !!this.config.productAvailable);
    if (stockBadge instanceof HTMLElement) stockBadge.hidden = !inStock;

    // Delivery promise only makes sense for something that can be bought right now.
    const delivery = this.querySelector('[data-lc-delivery]');
    if (delivery instanceof HTMLElement) delivery.hidden = !inStock;
  }

  /** @param {ReturnType<LcPdpOptions['setup']>} s */
  renderSteps(s) {
    const st = this.state;
    const t = this.config.text;
    const entries = this.entries();
    const label = (text) => `<div class="lc-obx__step-label">${esc(text)}</div>`;

    const colourStep = entries.length
      ? `<div class="lc-obx__step">
          ${label(st.colour && s.colourOk ? `${t.stepColour || 'Colour'}: ${st.colour}` : t.stepColour || 'Colour')}
          <div class="lc-obx__colours">
            ${entries
              .map((e) => {
                const soldOut = this.isSoldOut(e);
                return `<button type="button" class="lc-obx__colour" data-action="colour" data-value="${esc(e.name)}" aria-pressed="${same(st.colour, e.name)}" ${soldOut ? 'data-sold-out="true"' : ''}><span class="lc-swatch" style="background:${esc(e.hex || swatchHex(e.name))}"></span><span class="lc-obx__colour-text">${esc(e.name)}</span>${soldOut ? `<span class="lc-obx__colour-tag">${esc(t.soldOut || 'Sold out')}</span>` : ''}</button>`;
              })
              .join('')}
          </div>
        </div>`
      : '';

    const valueStep = s.values.length
      ? `<div class="lc-obx__step">
          ${label(t.stepOption || 'Pack size')}
          <div class="lc-obx__opts" style="--lc-obx-cols:${Math.min(s.values.length, 3)}">
            ${s.values
              .map((v) => {
                const variant = this.variantFor(st.colour, v);
                const price = variant ? this.money(variant.price) : '';
                const sub = this.unitFor(v, variant) || this.noteFor(v);
                // Sold-out pack sizes (or every size of a sold-out colour) are greyed out and not
                // selectable; the colour chip itself stays clickable so the shopper can reach the
                // "Sold out — back soon" notice and Notify me.
                const soldOut = s.soldOutColour || (variant && !variant.available);
                return `<button type="button" class="lc-obx__opt" data-action="value" data-value="${esc(v)}" aria-pressed="${same(st.value, v)}" ${soldOut ? 'data-sold-out="true" disabled aria-disabled="true"' : ''}>
                  <span class="lc-obx__opt-label">${esc(this.labelFor(v))}</span>
                  ${price ? `<span class="lc-obx__opt-price">${price}</span>` : ''}
                  ${soldOut ? `<span class="lc-obx__opt-sub">${esc(t.soldOut || 'Sold out')}</span>` : sub ? `<span class="lc-obx__opt-sub">${esc(sub)}</span>` : ''}
                  ${this.isBestValue(v) ? `<span class="lc-obx__best lc-badge lc-badge--orange">${esc(t.bestValue || 'Best value')}</span>` : ''}
                </button>`;
              })
              .join('')}
          </div>
        </div>`
      : '';

    return this.config.colourFirst ? colourStep + valueStep : valueStep + colourStep;
  }

  /** @param {ReturnType<LcPdpOptions['setup']>} s */
  renderResult(s) {
    const st = this.state;
    const t = this.config.text;
    const bits = [];
    if (t.resultPrefix) bits.push(t.resultPrefix);
    else if (st.value && s.valueOk) bits.push(this.labelFor(st.value));
    if (st.colour && s.colourOk) bits.push(st.colour);
    const title = bits.length ? bits.join(' · ') : t.notChosen || 'Not chosen yet';
    const total = s.price * st.qty;
    const canBuy = s.ready && s.variantId && s.available && !st.busy;

    const soonLabel = s.soldOutColour ? t.soldOutColour || 'Sold out — back soon' : t.comingSoon || 'Price coming soon';
    // The shipping note beside the price is a purchase promise, so it only shows for an available selection.
    const shipNote = t.shipNote && s.variantId && s.available ? `<span class="lc-obx__ship">${esc(t.shipNote)}</span>` : '';
    const priceRow = s.preview || s.soldOutColour
      ? `<div class="lc-obx__price-row"><span class="lc-obx__price lc-obx__price--soon">${esc(soonLabel)}</span></div>`
      : `<div class="lc-obx__price-row">
           <span class="lc-obx__price">${this.money(total)}</span>
           ${s.compare ? `<span class="lc-obx__was">${this.money(s.compare * st.qty)}</span>` : ''}
           ${shipNote}
         </div>`;

    const buyRow = s.preview || s.soldOutColour
      ? `<a class="lc-btn lc-btn--outline lc-btn--lg lc-btn--full lc-obx__cta" href="${esc(this.config.notifyUrl || '#')}">${esc(t.notifyLabel || 'Notify me')}</a>`
      : `${!s.variantId ? `<div class="lc-obx__error">${esc(t.noVariant || 'That combination is not available.')}</div>` : ''}
         ${s.variantId && !s.available ? `<div class="lc-obx__error">${esc(t.unavailable || 'This option is currently sold out.')}</div>` : ''}
         ${st.error ? `<div class="lc-obx__error">${esc(st.error)}</div>` : ''}
         <div class="lc-obx__buy-row">
           <div class="lc-obx__qty" role="group" aria-label="Quantity">
             <button type="button" data-action="qty" data-value="-1" aria-label="Decrease quantity">${MINUS}</button>
             <span class="lc-obx__qty-value">${st.qty}</span>
             <button type="button" data-action="qty" data-value="1" aria-label="Increase quantity">${PLUS}</button>
           </div>
           ${ctaButton('lc-btn lc-btn--primary lc-btn--lg lc-btn--full lc-obx__cta', canBuy, st, t)}
         </div>`;

    return `
      <div class="lc-obx__result-eyebrow">${esc(t.resultEyebrow || 'Your selection')}</div>
      <div class="lc-obx__result-title">${esc(title)}</div>
      ${priceRow}
      ${buyRow}
      ${t.fitNote ? `<div class="lc-obx__fit">${CHECK}<span>${esc(t.fitNote)}</span></div>` : ''}`;
  }

  /** Mobile sticky Add to Cart once the buy panel has scrolled out of view. */
  /** @param {ReturnType<LcPdpOptions['setup']>} s */
  renderSticky(s) {
    const bar = this.querySelector('[data-lc-sticky]');
    if (!(bar instanceof HTMLElement)) return;
    const st = this.state;
    const t = this.config.text;
    const show = this.isMobile && !this.#panelVisible && s.ready && !s.preview && !s.soldOutColour;
    bar.hidden = !show;
    if (!show) return;
    const bits = [];
    if (t.resultPrefix) bits.push(t.resultPrefix);
    else if (st.value) bits.push(this.labelFor(st.value));
    if (st.colour) bits.push(st.colour);
    const canBuy = s.variantId && s.available && !st.busy;
    bar.innerHTML = `
      <div class="lc-obx__sticky-text">
        <div class="lc-obx__sticky-title">${esc(bits.join(' · ') + (st.qty > 1 ? ` × ${st.qty}` : ''))}</div>
        <div class="lc-obx__sticky-price">${this.money(s.price * st.qty)}</div>
      </div>
      ${ctaButton('lc-btn lc-btn--primary', canBuy, st, t)}`;
  }

  #watchBuyPanel() {
    const panel = this.querySelector('.lc-obx__panel');
    if (!panel || !('IntersectionObserver' in window)) return;
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) this.#panelVisible = entry.isIntersecting;
        this.renderSticky(this.setup());
      },
      { threshold: 0 }
    );
    this.#observer.observe(panel);
  }

  // ------------------------------------------------------------------ events

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-action]') : null;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;
    const { action, value = '' } = target.dataset;

    switch (action) {
      case 'colour': {
        // Keep the chosen size/pack when the new colour's product offers it in stock; otherwise
        // move to the first in-stock size (or the first size for a fully sold-out colour).
        const values = this.values(value);
        const inStock = (/** @type {string} */ v) => this.variantFor(value, v)?.available;
        const keep = this.state.value && values.some((v) => same(v, this.state.value) && inStock(v));
        this.setState({ colour: value, value: keep ? this.state.value : values.find(inStock) ?? values[0] ?? null, added: false, error: '' });
        break;
      }
      case 'value': {
        const variant = this.variantFor(this.state.colour, value);
        if (variant && !variant.available) break;
        this.setState({ value, added: false, error: '' });
        break;
      }
      case 'qty': {
        const next = Math.min(99, Math.max(1, this.state.qty + Number(value)));
        this.setState({ qty: next, added: false });
        break;
      }
      case 'add':
        this.#add();
        break;
      default:
        break;
    }
  };

  // ------------------------------------------------------------------ cart

  async #add() {
    const s = this.setup();
    if (!s.ready || !s.variantId || !s.available) return;
    const items = [{ id: s.variantId, quantity: this.state.qty }];
    // The add request also renders sections/lc-cart-state.liquid, which carries the post-add cart as
    // JSON, so the cart bubble and drawer get the full cart without a second round trip.
    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean)
      .concat(CART_STATE_SECTION)
      .join(',');

    this.setState({ busy: true, error: '' });
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections }),
      });
      if (!response.ok) throw new Error('The cart is temporarily unavailable. Please try again.');
      const json = await response.json();
      let cart = cartFromSections(json.sections);
      if (!cart) {
        // Fallback (add rejected, or the state section missing): read the cart the classic way.
        const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        cart = await cartResponse.json();
      }
      if (json.status) {
        this.dispatchEvent(new CartErrorEvent({ error: json.message || 'Add to cart failed', code: 'INVALID' }));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'lc-pdp-options', sourceId: this.id, itemCount: items[0].quantity } });
        throw new Error(json.description || json.message || 'Add to cart failed');
      }
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'lc-pdp-options', sourceId: this.id, itemCount: items[0].quantity, sections: json.sections, didError: false } });
      this.setState({ busy: false, added: true });
    } catch (error) {
      deferred.reject(error);
      this.setState({ busy: false, error: error instanceof Error ? error.message : String(error) });
    }
  }
}

/** Section id rendered alongside the cart drawer by the add request; it outputs the cart as JSON. */
const CART_STATE_SECTION = 'lc-cart-state';

/**
 * Reads the post-add cart (same shape as /cart.js) out of the rendered lc-cart-state section.
 * @param {Record<string, string> | undefined} sections
 * @returns {any | null}
 */
function cartFromSections(sections) {
  try {
    const html = sections && sections[CART_STATE_SECTION];
    if (!html) return null;
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-lc-cart-state]');
    const cart = node ? JSON.parse(node.textContent || '') : null;
    return cart && Array.isArray(cart.items) && typeof cart.item_count === 'number' ? cart : null;
  } catch {
    return null;
  }
}

/**
 * Add-to-cart button. While the request is in flight it reads "Adding…" with a spinner and keeps its
 * primary colour (class lc-btn--busy), so the tap is acknowledged before the network answers.
 * @param {string} className
 * @param {boolean} canBuy
 * @param {{ busy: boolean, added: boolean }} st
 * @param {Record<string, string>} t
 */
function ctaButton(className, canBuy, st, t) {
  const label = st.busy ? t.addingLabel || 'Adding…' : st.added ? t.addedLabel || '✓ Added to cart' : t.addLabel || 'Add to cart';
  return `<button type="button" class="${className}${st.busy ? ' lc-btn--busy' : ''}" data-action="add" ${canBuy ? '' : 'disabled'}${st.busy ? ' aria-busy="true"' : ''}>${st.busy ? '<span class="lc-spinner" aria-hidden="true"></span>' : ''}${esc(label)}</button>`;
}

if (!customElements.get('lc-pdp-options')) customElements.define('lc-pdp-options', LcPdpOptions);
